import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';
import { PROFILE_IMAGES } from '../constants/profileImages';
import { getCached, clearCache } from '../functions/swipeCache';

function GnIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':    return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'plus':    return <svg {...p}><path d="M12 5v14M5 12h14"/></svg>;
        case 'search':  return <svg {...p}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
        case 'mail':    return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'chevron': return <svg {...p}><path d="m9 18 6-6-6-6"/></svg>;
        case 'bell':    return <svg {...p}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>;
        case 'shield':  return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        default: return null;
    }
}

export default function Gangs() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [profileInfo, setProfileInfo] = useState({ id: 0, username: '', profile: 0 });
    const [gangs, setGangs]             = useState([]);
    const [notifications, setNotifications] = useState({ invitations: [], winegangs: [] });
    const [ready, setReady]             = useState(false);
    const [fetchError, setFetchError]   = useState(false);
    const [retryKey, setRetryKey]       = useState(0);

    // Slide-in direction from swipe
    const swipeClass = useRef(
        sessionStorage.getItem('swipeDir') === 'from-right' ? ' gn-swipe-in' : ''
    );
    useEffect(() => { sessionStorage.removeItem('swipeDir'); }, []);

    useEffect(() => {
        const stored = localStorage.getItem('userLogged');
        if (stored) {
            const u = JSON.parse(stored);
            setProfileInfo({ id: u.id || 0, username: u.username || '', profile: u.profile || 0 });
        }
    }, []);

    useEffect(() => {
        if (!profileInfo.id) return;
        setFetchError(false);
        const userUrl  = `${process.env.REACT_APP_API_URL}/user/${profileInfo.id}`;
        const notifUrl = `${process.env.REACT_APP_API_URL}/hasNotifications/${profileInfo.id}`;

        // Use prefetch cache if available
        const cachedUser  = getCached(userUrl);
        const cachedNotif = getCached(notifUrl);
        if (cachedUser) {
            setGangs(cachedUser.winegangs || []);
            if (cachedNotif) setNotifications({ invitations: cachedNotif.invitations || [], winegangs: cachedNotif.winegangs || [] });
            clearCache(userUrl);
            clearCache(notifUrl);
            requestAnimationFrame(() => setReady(true));
            return;
        }

        const xhrUser = new XMLHttpRequest();
        xhrUser.open('GET', userUrl, true);
        xhrUser.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    const data = JSON.parse(this.responseText);
                    setGangs(data.winegangs || []);
                    requestAnimationFrame(() => setReady(true));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhrUser.send();

        const xhrNotif = new XMLHttpRequest();
        xhrNotif.open('GET', notifUrl, true);
        xhrNotif.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const data = JSON.parse(this.responseText);
                setNotifications({ invitations: data.invitations || [], winegangs: data.winegangs || [] });
            }
        };
        xhrNotif.send();
    }, [profileInfo.id, retryKey]);

    // Reverse swipe → back to home
    const swipeTouchX = useRef(null);
    const swipeTouchY = useRef(null);
    const handleTouchStart = (e) => { swipeTouchX.current = e.touches[0].clientX; swipeTouchY.current = e.touches[0].clientY; };
    const handleTouchEnd   = (e) => {
        if (swipeTouchX.current === null) return;
        const dx = e.changedTouches[0].clientX - swipeTouchX.current;
        const dy = e.changedTouches[0].clientY - swipeTouchY.current;
        swipeTouchX.current = null;
        if (window.innerWidth >= 1100) return;
        if (Math.abs(dy) > Math.abs(dx) * 0.8) return;
        if (dx < -75) { sessionStorage.setItem('swipeDir', 'from-left'); navigate('/'); }
    };

    const hasGangNotif = (gangId) =>
        notifications.winegangs.some(w => Number(w.winegang_id) === Number(gangId));

    const hasInviteNotif = notifications.invitations.length > 0;
    const avatarSrc = PROFILE_IMAGES[profileInfo.profile] || PROFILE_IMAGES[0];

    return (
        <div
            className={`gn-stage${ready ? ' gn-ready' : ''}${swipeClass.current}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div className="gn-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="gn-vignette" />
            <div className="gn-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            {/* Header */}
            <div className="gn-header">
                <button className="gn-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <GnIcon name="back" size={18} />
                </button>
                <div className="gn-header-center">
                    <GnIcon name="shield" size={15} />
                    <span className="gn-header-title">WineGang</span>
                </div>
                <Link to={`/user/${profileInfo.id}`} className="gn-avatar-btn">
                    <img src={avatarSrc} alt="" className="gn-avatar" />
                </Link>
            </div>

            <div className="gn-scroll">

                {/* My Gangs */}
                <section className="gn-section">
                    <div className="gn-section-label">
                        {gangs.length > 0 ? `${gangs.length} gang${gangs.length !== 1 ? 's' : ''}` : '—'}
                    </div>

                    {gangs.length > 0 ? (
                        <div className="gn-gang-list">
                            {gangs.map((gang, i) => {
                                const hasNotif = hasGangNotif(gang.id);
                                return (
                                    <button
                                        key={gang.id}
                                        className="gn-gang-card"
                                        style={{ animationDelay: `${i * 0.06}s` }}
                                        onClick={() => navigate(`/gangs/${gang.id}`)}
                                    >
                                        <div className="gn-gang-icon">
                                            <GnIcon name="shield" size={18} />
                                        </div>
                                        <span className="gn-gang-name">{gang.winegang_name}</span>
                                        <div className="gn-gang-right">
                                            {hasNotif && <span className="gn-notif-dot" />}
                                            <GnIcon name="chevron" size={16} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gn-empty">
                            <GnIcon name="shield" size={32} />
                            <p>{t('gangs.empty_state')}</p>
                        </div>
                    )}
                </section>

                {/* Actions */}
                <section className="gn-actions">
                    <Link to="/gangs/crear" className="gn-action-primary">
                        <GnIcon name="plus" size={18} />
                        <span>{t('gangs.create')}</span>
                    </Link>

                    <div className="gn-action-row">
                        <Link to="/gangs/buscar" className="gn-action-sec">
                            <GnIcon name="search" size={17} />
                            <span>{t('gangs.search')}</span>
                        </Link>
                        <Link to="/gangs/invitaciones" className="gn-action-sec">
                            <div className="gn-action-icon-wrap">
                                <GnIcon name="mail" size={17} />
                                {hasInviteNotif && <span className="gn-notif-dot gn-notif-mail" />}
                            </div>
                            <span>{t('gangs.invitations')}</span>
                        </Link>
                    </div>
                </section>

            </div>
        </div>
    );
}
